import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TODAY } from "@/data/mock";
import { brl } from "@/lib/format";

export const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs font-medium uppercase tracking-wider text-neutral-500">{label}</Label>
    {children}
  </div>
);

const PeopleSelect = ({ value, onChange, people, allowNone, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder="Selecione" /></SelectTrigger>
    <SelectContent>
      {allowNone && <SelectItem value="none">Venda geral da empresa</SelectItem>}
      {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
    </SelectContent>
  </Select>
);

export function SalesEntryForm({ companyId, people, onSubmit }) {
  const [form, setForm] = useState({ date: TODAY, value: "", collaboratorId: "none", note: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    const value = Number(form.value);
    if (!value || value <= 0) return toast.error("Informe um valor de venda válido.");
    if (form.date < `${TODAY.slice(0, 8)}01` || form.date > TODAY) return toast.error("Escolha uma data do mês atual até hoje.");
    onSubmit({ companyId, date: form.date, value, collaboratorId: form.collaboratorId === "none" ? null : form.collaboratorId, note: form.note || "Lançamento manual" });
    toast.success(`Venda de ${brl(value)} lançada com sucesso.`);
    setForm((f) => ({ ...f, value: "", note: "" }));
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="sales-entry-form">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Data"><Input type="date" min={`${TODAY.slice(0, 8)}01`} max={TODAY} value={form.date} onChange={set("date")} data-testid="sales-date-input" /></Field>
        <Field label="Valor total (R$)"><Input type="number" min="0" step="0.01" placeholder="0,00" value={form.value} onChange={set("value")} data-testid="sales-value-input" /></Field>
      </div>
      <Field label="Colaboradora"><PeopleSelect value={form.collaboratorId} onChange={set("collaboratorId")} people={people} allowNone testId="sales-collaborator-select" /></Field>
      <Field label="Observação"><Input placeholder="Ex.: total do dia no sistema de vendas" value={form.note} onChange={set("note")} data-testid="sales-note-input" /></Field>
      <Button type="submit" className="w-full font-semibold transition-transform hover:-translate-y-0.5" data-testid="sales-submit-button">Lançar venda</Button>
    </form>
  );
}

export function NewTaskDialog({ companyId, people, onCreate }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", collaboratorId: people[0]?.id, time: "09:00", priority: "Média" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Descreva a tarefa.");
    onCreate({ ...form, companyId });
    toast.success("Tarefa atribuída com sucesso.");
    setForm((f) => ({ ...f, title: "" }));
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-task-button" className="font-semibold"><Plus className="mr-1 h-4 w-4" /> Nova tarefa</Button>
      </DialogTrigger>
      <DialogContent data-testid="new-task-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Atribuir nova tarefa</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <Field label="Tarefa"><Input value={form.title} onChange={set("title")} placeholder="Ex.: Revisar vitrine" data-testid="new-task-title-input" /></Field>
          <Field label="Colaboradora"><PeopleSelect value={form.collaboratorId} onChange={set("collaboratorId")} people={people} testId="new-task-collaborator-select" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Horário"><Input type="time" value={form.time} onChange={set("time")} data-testid="new-task-time-input" /></Field>
            <Field label="Prioridade">
              <Select value={form.priority} onValueChange={set("priority")}>
                <SelectTrigger data-testid="new-task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Alta", "Média", "Baixa"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Button type="submit" className="w-full font-semibold" data-testid="new-task-submit-button">Salvar tarefa</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function FeedbackForm({ companyId, people, from, onSubmit }) {
  const [form, setForm] = useState({ collaboratorId: people[0]?.id, type: "Elogio", text: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.text.trim()) return toast.error("Escreva o feedback.");
    onSubmit({ ...form, companyId, from, date: TODAY });
    toast.success("Feedback enviado.");
    setForm((f) => ({ ...f, text: "" }));
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="feedback-form">
      <Field label="Para"><PeopleSelect value={form.collaboratorId} onChange={set("collaboratorId")} people={people} testId="feedback-collaborator-select" /></Field>
      <Field label="Tipo">
        <Select value={form.type} onValueChange={set("type")}>
          <SelectTrigger data-testid="feedback-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["Elogio", "Desenvolvimento", "Reconhecimento"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Mensagem"><Textarea rows={4} value={form.text} onChange={set("text")} placeholder="Escreva um feedback claro e construtivo" data-testid="feedback-text-input" /></Field>
      <Button type="submit" className="w-full font-semibold" data-testid="feedback-submit-button">Enviar feedback</Button>
    </form>
  );
}
